import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import { getStudents } from '../../apis/users';
import { getSchoolClassesConfig } from '../../apis/schoolConfig';
import consolidatedMarksApis from '../../apis/consolidatedMarks';
import postsApis from '../../apis/posts';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { RootState } from '../../redux/store';
import { SchoolClassesConfig } from '../../types/schoolConfigType';
import ConsolidatedMarksReportPage from './ConsolidatedMarksReportPage';
import { User } from '../../types/userType';
import {
  downloadFileFromUrl,
  downloadMessagePdf
} from '../../utils/fileGeneration';
import { ReportCardDispatch } from '../../types/reportCardDispatchType';
import { downloadReport } from '../../utils/reportUtils';
import buildReportCardFileName from '../../utils/reportCardFileName';
import { canPostReportCardToParents } from '../../constants/reportCard';

const ConsolidatedMarksReportContainer: React.FC = () => {
  const [allClasses, setAllClasses] = useState<SchoolClassesConfig>();
  const [isClassesLoading, setIsClassesLoading] = useState<boolean>(false);
  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );
  const [studentDetails, setStudentDetails] = useState<User[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloadInProgress, setIsDownloadInProgress] = useState(false);
  // Ids of the rows currently being sent. A list rather than a single id so two rows can be sent
  // at the same time, and so one row's send never disables the other rows' buttons.
  const [sendingStudentIds, setSendingStudentIds] = useState<string[]>([]);
  // Same idea for a single row's "Generate Report Card": generating one student's card must not
  // disable every other row. isDownloadInProgress stays for the whole-table operations only.
  const [generatingStudentIds, setGeneratingStudentIds] = useState<string[]>(
    []
  );
  const [isSendAllInProgress, setIsSendAllInProgress] = useState(false);
  // studentId -> last send record, so the table remembers across logins what was already sent.
  const [dispatchByStudentId, setDispatchByStudentId] = useState<
    Record<string, ReportCardDispatch>
  >({});

  // Keeps the row's status current straight after a send, without re-querying the whole class.
  const markStudentSent = useCallback((studentId: string) => {
    setDispatchByStudentId((previous) => ({
      ...previous,
      [studentId]: {
        ...previous[studentId],
        studentId,
        status: 'SENT',
        lastSentAt: new Date().toISOString()
      }
    }));
  }, []);

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const showToast = useCallback(
    (message: string, severity: SnackbarSeverityType) => {
      setToastMessage(message);
      setToastSeverity(severity);
      setToastOpen(true);
    },
    []
  );

  useEffect(() => {
    setIsClassesLoading(true);
    getSchoolClassesConfig(loggedInUser.school)
      .then((response) => {
        setAllClasses(response.data as unknown as SchoolClassesConfig);
      })
      .catch((error) => {
        console.error(error);
        showToast(
          'Failed to load school configuration.',
          SnackbarSeverityType.error
        );
      })
      .finally(() => setIsClassesLoading(false));
  }, [loggedInUser.school, showToast]);

  const handleSearch = useCallback(
    async (academicYear: string, grade: string, division: string) => {
      setIsLoading(true);
      try {
        // Fetch current students list
        const students = await getStudents(
          loggedInUser.school,
          academicYear,
          grade,
          division
        );
        setStudentDetails(students);

        // Only schools with the workflow enabled have send history to fetch.
        if (!canPostReportCardToParents(loggedInUser.school)) {
          setDispatchByStudentId({});
          return;
        }

        // Separate try/catch: the students list is the point of the search, so a missing
        // send-history must not blank the table - the column just falls back to "Not Sent".
        try {
          const response = await postsApis.getReportCardDispatches(
            academicYear,
            grade,
            division
          );
          const dispatches: ReportCardDispatch[] = response?.data || [];
          setDispatchByStudentId(
            dispatches.reduce(
              (acc, dispatch) => ({ ...acc, [dispatch.studentId]: dispatch }),
              {} as Record<string, ReportCardDispatch>
            )
          );
        } catch (error) {
          console.error('Failed to fetch report card send history:', error);
          setDispatchByStudentId({});
        }
      } catch (error) {
        console.error('Failed to fetch students:', error);
        showToast('Failed to fetch students.', SnackbarSeverityType.error);
        setStudentDetails([]);
        setDispatchByStudentId({});
      } finally {
        setIsLoading(false);
      }
    },
    [loggedInUser.school, showToast]
  );

  const handleGenerateConsolidatedReport = useCallback(
    async (
      academicYear: string,
      grade: string,
      division: string,
      terms: string[]
    ) => {
      try {
        await consolidatedMarksApis.generateConsolidatedReport(
          academicYear,
          grade,
          division,
          terms,
          loggedInUser.school,
          loggedInUser.id
        );
        showToast(
          'Report generation started. You will be able to download it in 15 minutes.',
          SnackbarSeverityType.success
        );
      } catch (error) {
        console.error('Failed to generate consolidated report:', error);
        showToast(
          'Failed to generate consolidated report.',
          SnackbarSeverityType.error
        );
      }
    },
    [loggedInUser.school, loggedInUser.id, showToast]
  );

  const handleDownloadConsolidatedReport = useCallback(
    async (academicYear: string, grade: string, division: string) => {
      try {
        const response = await consolidatedMarksApis.getConsolidatedMarksReport(
          academicYear,
          grade,
          division,
          loggedInUser.school
        );
        downloadReport(
          response.data,
          response.fileName ||
            `Consolidated Marks Report - ${moment().format(
              'YYYY-MM-DD_HH-mm-ss'
            )}`,
          'Consolidated Marks Report',
          showToast
        );
      } catch (error) {
        console.error('Failed to download consolidated report:', error);
        showToast(
          'Failed to download consolidated report.',
          SnackbarSeverityType.error
        );
      }
    },
    [loggedInUser.school, showToast]
  );

  const handleDownloadAllReportCards = useCallback(
    async (
      academicYear: string,
      reportCardDate: string,
      termNames: string[]
    ) => {
      if (!studentDetails || studentDetails.length === 0) {
        showToast(
          'No students found to download report cards for.',
          SnackbarSeverityType.success
        );
        return;
      }

      setIsDownloadInProgress(true);
      let successfulDownloads = 0;
      let placeholderDownloads = 0;
      let failedDownloads = 0;

      for (const student of studentDetails) {
        const fallbackFileName = `report_card-${
          student.enrollmentNo || student.id
        }`;
        try {
          const response = await consolidatedMarksApis.generateReportCard(
            student.id,
            academicYear,
            reportCardDate
          );
          const { reportCardUrl, fileName } = response;
          if (!reportCardUrl) {
            throw new Error('Report card URL was not returned by the server.');
          }
          downloadFileFromUrl(
            reportCardUrl,
            buildReportCardFileName(
              student.name,
              termNames,
              academicYear,
              fileName
            ),
            'pdf'
          );
          successfulDownloads++;
        } catch (error) {
          console.error(
            `Failed to generate report card for student ${student.id}:`,
            error
          );
          // Keep one PDF per student in the bulk export: instead of skipping (or
          // emitting a blank file), write the backend reason into a PDF — e.g.
          // "No marks have been entered for this student <name>".
          const reason = (error as { message?: string })?.message;
          if (reason) {
            downloadMessagePdf(fallbackFileName, reason);
            placeholderDownloads++;
          } else {
            failedDownloads++;
          }
        }
      }
      setIsDownloadInProgress(false);

      if (successfulDownloads > 0 || placeholderDownloads > 0) {
        const parts = [];
        if (successfulDownloads > 0) {
          parts.push(`${successfulDownloads} report card(s)`);
        }
        if (placeholderDownloads > 0) {
          parts.push(`${placeholderDownloads} notice(s) for students without marks`);
        }
        showToast(
          `Started download for ${parts.join(' and ')}.`,
          SnackbarSeverityType.success
        );
      }
      if (failedDownloads > 0) {
        showToast(
          `Failed to download ${failedDownloads} report card(s). Check console for details.`,
          SnackbarSeverityType.error
        );
      }
      if (
        successfulDownloads === 0 &&
        placeholderDownloads === 0 &&
        failedDownloads === 0
      ) {
        showToast(
          'No report cards were downloaded.',
          SnackbarSeverityType.success
        );
      }
    },
    [studentDetails, showToast]
  );

  const handleGenerateReportCard = useCallback(
    async (
      studentId: string,
      studentName: string,
      academicYear: string,
      reportCardDate: string,
      termNames: string[]
    ) => {
      setIsDownloadInProgress(true);
      try {
        const response = await consolidatedMarksApis.generateReportCard(
          studentId,
          academicYear,
          reportCardDate
        );
        const { reportCardUrl, fileName } = response;
        if (!reportCardUrl) {
          throw new Error('Report card URL was not returned by the server.');
        }
        const student = studentDetails?.find(
          (candidate) => candidate.id === studentId
        );
        downloadFileFromUrl(
          reportCardUrl,
          buildReportCardFileName(
            student?.name || '',
            termNames,
            academicYear,
            fileName
          ),
          'pdf'
        );
        showToast(
          'Report card download started successfully.',
          SnackbarSeverityType.success
        );
      } catch (error) {
        console.error(
          `Failed to generate report card for ${studentName} (${studentId}):`,
          error
        );
        // The backend messages are already user-facing and student-specific
        // (e.g. "No marks have been entered for this student <name>"), so show
        // them as-is; fall back to a generic, named message otherwise.
        const reason = (error as { message?: string })?.message;
        showToast(
          reason || `Failed to generate report card for ${studentName}.`,
          SnackbarSeverityType.error
        );
      } finally {
        setIsDownloadInProgress(false);
      }
      setGeneratingStudentIds((previous) =>
        previous.filter((id) => id !== studentId)
      );
    },
    [showToast, studentDetails]
  );

  // Generates the student's report card and shares that same PDF with the family as an ecole post.
  // The PDF has to be generated first: exam-be renders it on demand and ether-be needs a real URL
  // to pull into the school's storage before it can attach it to the post.
  const handleSendReportCardToParent = useCallback(
    async (
      studentId: string,
      academicYear: string,
      reportCardDate: string,
      termNames: string[]
    ) => {
      setSendingStudentIds((previous) => [...previous, studentId]);
      try {
        const { reportCardUrl, fileName } =
          await consolidatedMarksApis.generateReportCard(
            studentId,
            academicYear,
            reportCardDate
          );

        if (!reportCardUrl) {
          throw new Error('Report card could not be generated.');
        }

        await postsApis.sendReportCardToParent(
          studentId,
          reportCardUrl,
          academicYear,
          fileName,
          termNames
        );
        markStudentSent(studentId);
        showToast(
          'Report card sent to the parent successfully.',
          SnackbarSeverityType.success
        );
      } catch (error) {
        console.error('Failed to send report card to parent:', error);
        showToast(
          'Failed to send report card to the parent.',
          SnackbarSeverityType.error
        );
      }
      setSendingStudentIds((previous) =>
        previous.filter((id) => id !== studentId)
      );
    },
    [showToast, markStudentSent]
  );

  // Bulk counterpart of handleSendReportCardToParent, mirroring handleDownloadAllReportCards:
  // students are processed one at a time so a single failure doesn't abort the rest, and the
  // toast reports how many actually went out.
  const handleSendAllReportCardsToParents = useCallback(
    async (
      academicYear: string,
      reportCardDate: string,
      termNames: string[]
    ) => {
      if (!studentDetails || studentDetails.length === 0) {
        showToast(
          'No students found to send report cards for.',
          SnackbarSeverityType.success
        );
        return;
      }

      setIsSendAllInProgress(true);
      let successfulSends = 0;
      let failedSends = 0;

      for (const student of studentDetails) {
        try {
          const { reportCardUrl, fileName } =
            await consolidatedMarksApis.generateReportCard(
              student.id,
              academicYear,
              reportCardDate
            );

          if (!reportCardUrl) {
            throw new Error('Report card could not be generated.');
          }

          await postsApis.sendReportCardToParent(
            student.id,
            reportCardUrl,
            academicYear,
            fileName,
            termNames
          );
          markStudentSent(student.id);
          successfulSends++;
        } catch (error) {
          console.error(
            `Failed to send report card for student ${student.id}:`,
            error
          );
          failedSends++;
        }
      }
      setIsSendAllInProgress(false);

      // One combined toast rather than a success one followed by an error one: showToast holds a
      // single message, so firing twice would immediately overwrite the first and the count would
      // never be seen.
      const totalStudents = studentDetails.length;
      const studentCount = (count: number) =>
        `${count} student${count === 1 ? '' : 's'}`;

      if (failedSends === 0) {
        showToast(
          `Report card sent to ${studentCount(successfulSends)}.`,
          SnackbarSeverityType.success
        );
      } else if (successfulSends === 0) {
        showToast(
          `Failed to send the report card to all ${studentCount(
            totalStudents
          )}. Check console for details.`,
          SnackbarSeverityType.error
        );
      } else {
        showToast(
          `Report card sent to ${successfulSends} of ${studentCount(
            totalStudents
          )}. ${failedSends} failed - check console for details.`,
          SnackbarSeverityType.error
        );
      }
    },
    [studentDetails, showToast, markStudentSent]
  );

  return (
    <>
      <ConsolidatedMarksReportPage
        allClasses={allClasses as SchoolClassesConfig}
        school={loggedInUser.school}
        isClassesLoading={isClassesLoading}
        studentDetails={studentDetails}
        isLoading={isLoading}
        onSearch={handleSearch}
        onGenerateConsolidatedReport={handleGenerateConsolidatedReport}
        onDownloadConsolidatedReport={handleDownloadConsolidatedReport}
        onDownloadAllReportCards={handleDownloadAllReportCards}
        onGenerateReportCard={handleGenerateReportCard}
        onSendReportCardToParent={handleSendReportCardToParent}
        onSendAllReportCardsToParents={handleSendAllReportCardsToParents}
        isSendAllInProgress={isSendAllInProgress}
        isDownloadInProgress={isDownloadInProgress}
        sendingStudentIds={sendingStudentIds}
        generatingStudentIds={generatingStudentIds}
        dispatchByStudentId={dispatchByStudentId}
        showToast={showToast}
      />
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default ConsolidatedMarksReportContainer;
